/**
 * transport/models.ts —— **模型目录面客户端**（入口侧四动词：列／增／改／删）。
 *
 * 由来（★20261005 报障：「设置面板添加模型无法保存」）：后端 `/api/models` 四动词**早已在位**
 * （`kernel/decl/models_store.py`；F4 已接 `seal`，密钥落库＝密文），但**前端从未接线**——
 * `SettingsPanel` 的 `onAddModel/onUpdateModel/onDeleteModel` 全仓无生产注入点、`transport/` 亦无模型面
 * ⇒ 点保存＝零请求、静默无反应（空壳）。本件＝那条缺的线。
 *
 * 纪律（同 `chatSessions.ts`）：路径经白名单（集合 `assertEgress`／单件 `assertEgressWithin`）；
 *   浏览器零凭据（同源 cookie 自动携带）；**失败显式抛**（携服务端信封 message，禁静默假成功）；
 *   密钥只上行不回显（响应结构本就不含密钥列）。
 */
import { assertEgress, assertEgressWithin } from "./whitelist";
import type { FetchLike } from "./session";
import { E_CODES } from "./errorText";

/** 服务端 `to_entry` 公开行（`kernel/decl/model_catalog.py::PUBLIC_FIELDS` 同形） */
export interface ModelRow {
  id: string;
  api_model?: string;
  display_name?: string;
  base_url?: string;
  provider?: string;
  thinking_effort?: string;
  max_output_tokens?: number | null;
  context_length?: number | null;
  priority?: number | null;
}

/** 录入表单值（与 `ModelCatalogSection.ModelFormValues` 同形；`apiKey` 只上行、不回显） */
export interface ModelForm {
  apiModel: string;
  displayName: string;
  baseUrl: string;
  apiKey?: string;
  thinkingEffort: string;
}

export class ModelWriteError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(status: number, message: string, code: string | null = null) {
    super(E_CODES.MODEL_WRITE + "： " + message);
    this.name = "ModelWriteError";
    this.status = status;
    this.code = code;
  }
}

async function readMessage(res: { status: number; json: () => Promise<unknown> }): Promise<{ message: string; code: string | null }> {
  try {
    const body = (await res.json()) as { message?: string; error?: string } | null;
    const message = (body && (body.message || body.error)) || "HTTP " + res.status;
    return { message: String(message), code: body && body.error ? String(body.error) : null };
  } catch {
    return { message: "HTTP " + res.status, code: null };
  }
}

async function call(path: string, init: RequestInit, fetchFn: FetchLike, what: string): Promise<unknown> {
  const res = await fetchFn(path, { credentials: "same-origin", ...init });
  if (!res.ok) {
    const { message, code } = await readMessage(res as unknown as { status: number; json: () => Promise<unknown> });
    throw new ModelWriteError(res.status, what + "被拒：" + message, code);
  }
  return res.json();
}

/** 列表（服务端排序＝`sort_order`；前端不再排一次） */
export async function listModels(fetchFn: FetchLike = fetch): Promise<ModelRow[]> {
  const path = assertEgress("models", "/api/models");
  const data = (await call(path, { method: "GET" }, fetchFn, "模型列表")) as ModelRow[];
  return Array.isArray(data) ? data : [];
}

/** 新增（`api_key` 非空 ⇒ 服务端经 `seal` 落密文；无 seal ⇒ 显式拒写并回错误信封） */
export async function createModel(values: ModelForm, fetchFn: FetchLike = fetch): Promise<ModelRow> {
  const path = assertEgress("models", "/api/models");
  return (await call(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      api_model: values.apiModel,
      display_name: values.displayName,
      base_url: values.baseUrl,
      api_key: values.apiKey || undefined,
      thinking_effort: values.thinkingEffort,
    }),
  }, fetchFn, "新增模型")) as ModelRow;
}

/** 修改（**留空 `api_key` ⇒ 不改密钥**——服务端口径：空串整列不动） */
export async function updateModel(id: string, values: ModelForm, fetchFn: FetchLike = fetch): Promise<ModelRow> {
  const path = assertEgressWithin("models", "/api/models/" + encodeURIComponent(id));
  return (await call(path, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      api_model: values.apiModel || undefined,
      display_name: values.displayName || undefined,
      base_url: values.baseUrl || undefined,
      api_key: values.apiKey || undefined,
      thinking_effort: values.thinkingEffort || undefined,
    }),
  }, fetchFn, "修改模型")) as ModelRow;
}

/** 删除（服务端物理删；被引为默认模型 ⇒ 拒，错误信封带回原因） */
export async function deleteModel(id: string, fetchFn: FetchLike = fetch): Promise<ModelRow> {
  const path = assertEgressWithin("models", "/api/models/" + encodeURIComponent(id));
  return (await call(path, { method: "DELETE" }, fetchFn, "删除模型")) as ModelRow;
}

/** 公开行 ⇒ 设置面板的供应商分组形态（`ModelProviderGroup`；provider 缺省归 `未标注`） */
export function toProviderGroups(rows: ModelRow[] | null | undefined): Array<{ provider: string; models: Array<{ id: string; label: string }> }> {
  const byProvider = new Map<string, Array<{ id: string; label: string }>>();
  for (const row of rows ?? []) {
    const provider = String(row.provider || "未标注");
    const label = String(row.display_name || row.api_model || row.id);
    if (!byProvider.has(provider)) byProvider.set(provider, []);
    byProvider.get(provider)!.push({ id: String(row.id), label });
  }
  return [...byProvider.entries()].map(([provider, models]) => ({ provider, models }));
}
